/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Component, { type ComponentAttrs } from 'flarum/common/Component';
import newReviewAction, { canStartReview } from '../utils/newReviewAction';
import type Mithril from 'mithril';
import type DiscussionListState from 'flarum/forum/states/DiscussionListState';
import type Article from 'flarum/orell-articles/models/Article';

export interface CreateReviewButtonAttrs extends ComponentAttrs {
	/** The article to review. Omitted when the button only has to open the article picker. */
	article?: Article;
	/** The list the new review should be inserted into, when rendered next to one. */
	reviewsState?: DiscussionListState;
	/** Overrides the default (permission-dependent) label. */
	label?: Mithril.Children;
}

/**
 * What every "write a review" button does, minus how it looks. The two subclasses exist for their
 * markup alone — CreateReviewActionButton has to match the DOM orell-articles styles for its
 * ArticleActionButtons rows — so everything else lives here and cannot drift apart.
 */
export default abstract class AbstractCreateReviewButton<T extends CreateReviewButtonAttrs = CreateReviewButtonAttrs> extends Component<T> {
	protected allowed(): boolean {
		return canStartReview();
	}

	/** The caller's label, or the default for what the actor is allowed to do. */
	protected label(): Mithril.Children {
		return (
			this.attrs.label ||
			(this.allowed()
				? app.translator.trans('glowingblue-orell-rezensionen.forum.reviews.create_review_button')
				: app.translator.trans('glowingblue-orell-rezensionen.forum.reviews.cannot_create_review_button'))
		);
	}

	protected start(): void {
		newReviewAction(this.attrs.article, this.attrs.reviewsState);
	}
}
